// 🏗🌅 «아침 안전 조회» 장표 6종 — 화면 전용(디렉터 어휘 밖 · EF 무개조 · kineticTypo 선례).
//
// 정본 화면 = 회장님 확정 v3(맥공유/산업안전/소장아침조회_지침 · «오케이 이 느낌으로» 10-10). 좌표·글자 크기·색은
//   v3 합성 코드(compose_v3.py)의 값을 1920×1080 기준 그대로 옮겼다 — 그래픽 칸 x 96~1060 · 흰 카드 반경 26 ·
//   제목 Bold 80 + 위 설명 SemiBold 38 · 카드 본문 Bold 58~60 · 보조 Medium 34.
// ★v3 와 다른 한 가지★: 제품 자막은 세이프존(바닥 82.87%) 때문에 v3(y 950)보다 위(띠 윗선 ≈ y 800)에 선다 → 모든 장표가
//   y 780 안에서 끝나도록 세로 간격만 줄였다(가로 좌표·글자 크기는 v3 그대로). 겹치면 장표 층(#6119)이 장표 전체를 줄여 버린다.
// 움직임의 시각 = 장면 문장 시간표(`beats` · 문장 k 의 시작/끝). 틀 조립기(`lib/agent/morning-call.ts`)가 사건과 문장
//   경계가 맞게 문장을 나눈다(v3 대사가 이미 그렇다 — 체크=그 질문 끝 · 강조=«굴착기 작업 구간» 시작 · 두 번째 카드=그 문장 시작).
import React from 'react';
import { useVideoConfig } from 'remotion';
import { SceneImg } from '../templates/SceneImg';
import type { SlideBeat } from './layouts';

export const MORNING = {
  ink: 'rgb(24,32,44)', sub: 'rgb(96,106,120)', red: 'rgb(214,58,47)', green: 'rgb(36,145,88)',
  card: '#FFFFFF', box: 'rgb(235,238,242)', chip: 'rgb(232,236,242)', dim: 'rgb(170,176,186)',
  tipBg: 'rgb(255,241,219)', tipInk: 'rgb(150,78,0)', sun: 'rgb(250,188,40)',
} as const;
const X0 = 96, X1 = 1060, R = 26;

type Base = { f: number; beats?: ReadonlyArray<SlideBeat> };

/** 문장 k 시작 프레임(없으면 대신값). */
export const beatStart = (beats: Base['beats'], k: number, fallback: number): number => beats?.[k]?.atFrame ?? fallback;
/** 문장 k 끝 프레임 — untilFrame · 다음 문장 시작 · 대신값 순. */
export const beatEnd = (beats: Base['beats'], k: number, fallback: number): number =>
  beats?.[k]?.untilFrame ?? beats?.[k + 1]?.atFrame ?? fallback;

/** v3 «아래 40px 에서 올라오며 나타남»(0.5초 · ease-out cubic). at 이전 = 안 보임. */
function rise(f: number, at: number, fps: number): React.CSSProperties {
  const x = Math.max(0, Math.min(1, (f - at) / (0.5 * fps)));
  const e = 1 - (1 - x) ** 3;
  return { opacity: e, transform: `translateY(${Math.round(40 * (1 - e))}px)` };
}

function useScale() {
  const { width, fps } = useVideoConfig();
  const S = width / 1920;
  return { fps, px: (v: number) => Math.round(v * S) };
}

const Abs: React.FC<{ x: number; y: number; w?: number; h?: number; px: (v: number) => number; style?: React.CSSProperties; children?: React.ReactNode }> = ({ x, y, w, h, px, style, children }) => (
  <div style={{ position: 'absolute', left: px(x), top: px(y), ...(w != null ? { width: px(w) } : {}), ...(h != null ? { height: px(h) } : {}), ...style }}>{children}</div>
);

/** 글자 폭 어림(em) — 한글·한자 1 · 숫자·라틴 0.6 · 띄어쓰기 0.28 · 문장부호 0.35. 측정(DOM) 없이 정해지는 값이라 렌더·받기·썸네일이 같은 크기를 낸다. */
export function estimateTextEm(line: string): number {
  let em = 0;
  for (const ch of line) {
    if (/\s/.test(ch)) em += 0.28;
    else if (/[\u1100-\u11ff\u3130-\u318f\uac00-\ud7af\u4e00-\u9fff]/.test(ch)) em += 1;
    else if (/[0-9A-Za-z°]/.test(ch)) em += 0.6;
    else em += 0.35;
  }
  return em;
}
/** 🌅 «글자 잘림 0» — 한 줄 글은 폭(디자인 px) 안에 들도록 글자 크기만 줄인다(가장 긴 줄 기준 · 하한 없음 — 잘리느니 작게). AI 가 쓴 칸 글 길이가 들쭉날쭉해도 카드 밖으로 넘지 않는다.
 *  너무 긴 칸 글은 서버 검사(`_shared/morning-chat.ts` 화면 글 길이)가 먼저 알린다. */
export function fitTextSize(text: string, size: number, maxW: number): number {
  const longest = Math.max(0, ...String(text ?? "").split("\n").map(estimateTextEm));
  if (longest <= 0) return size;
  return Math.min(size, Math.floor((maxW / longest) * 10) / 10);
}

const Txt: React.FC<{ size: number; weight: number; color: string; px: (v: number) => number; children: React.ReactNode; style?: React.CSSProperties; fitW?: number }> = ({ size, weight, color, px, children, style, fitW }) => {
  const s = fitW && typeof children === 'string' ? fitTextSize(children, size, fitW) : size;
  return <div style={{ fontSize: px(s), fontWeight: weight, color, lineHeight: 1.18, letterSpacing: `${px(-0.5)}px`, whiteSpace: 'pre-line', wordBreak: 'keep-all', ...style }}>{children}</div>;
};
const W = X1 - X0; // 카드 폭 964

/** 두 줄 카드 글 — 줄바꿈이 없으면 가운데에 가장 가까운 띄어쓰기에서 나눈다(3줄로 넘쳐 카드 밑을 뚫지 않게). */
export function twoLines(text: string): string {
  const t = String(text ?? "").trim();
  if (t.includes("\n") || estimateTextEm(t) <= 7.5) return t.split("\n").slice(0, 2).join("\n");
  const mid = t.length / 2;
  let best = -1;
  for (let i = 0; i < t.length; i++) if (t[i] === " " && (best < 0 || Math.abs(i - mid) < Math.abs(best - mid))) best = i;
  return best < 0 ? t : `${t.slice(0, best)}\n${t.slice(best + 1)}`;
}

/** 장면 머리 — 위 작은 설명(SemiBold 38 · 회색) + 제목(Bold 80). */
const Head: React.FC<{ kicker: string; title: string; px: (v: number) => number }> = ({ kicker, title, px }) => (
  <>
    <Abs x={X0} y={72} px={px}><Txt size={38} weight={600} color={MORNING.sub} px={px} fitW={W} style={{ whiteSpace: 'nowrap' }}>{kicker}</Txt></Abs>
    <Abs x={X0} y={122} px={px} w={X1 - X0}><Txt size={80} weight={700} color={MORNING.ink} px={px} fitW={W} style={{ whiteSpace: 'nowrap' }}>{title}</Txt></Abs>
  </>
);

const CheckBox: React.FC<{ x: number; y: number; on: boolean; px: (v: number) => number }> = ({ x, y, on, px }) => (
  <Abs x={x} y={y} w={70} h={70} px={px} style={{ borderRadius: px(14), background: on ? MORNING.green : MORNING.box }}>
    {on ? (
      <svg viewBox="0 0 70 70" width="100%" height="100%" aria-hidden>
        <polyline points="15,37 32,55 58,17" fill="none" stroke="#FFFFFF" strokeWidth={9} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) : null}
  </Abs>
);

/** ① 표지 — 날짜 · «아침 / 안전 조회» · 현장 이름 칩. */
export const MorningCover: React.FC<Base & { date: string; title?: string[]; site?: string }> = ({ f, date, title = ['아침', '안전 조회'], site }) => {
  const { fps, px } = useScale();
  return (
    <div style={{ position: 'absolute', inset: 0, ...rise(f, 0, fps) }}>
      <Abs x={X0} y={290} px={px}><Txt size={48} weight={600} color={MORNING.sub} px={px} fitW={W} style={{ whiteSpace: 'nowrap' }}>{date}</Txt></Abs>
      {title.slice(0, 2).map((t, i) => (
        <Abs key={i} x={X0} y={360 + i * 135} px={px}><Txt size={112} weight={700} color={MORNING.ink} px={px} fitW={W} style={{ whiteSpace: 'nowrap' }}>{t}</Txt></Abs>
      ))}
      {site ? (
        <Abs x={X0} y={650} h={64} px={px} style={{ borderRadius: px(18), background: MORNING.chip, padding: `0 ${px(26)}px`, display: 'flex', alignItems: 'center' }}>
          <Txt size={42} weight={600} color={MORNING.ink} px={px} fitW={W - 52} style={{ whiteSpace: 'nowrap' }}>{site}</Txt>
        </Abs>
      ) : null}
    </div>
  );
};

/** ② 기본 수칙 · ⑦ 구호 — 흰 카드 줄 + 빈 칸 → 그 질문 끝에 초록 체크(+왼쪽 초록 막대).
 *  mode 'basics' = 줄이 처음부터 서고 질문 k(문장 1+k) 끝 0.5초 전에 체크 · 'chant' = 문장 1 시작에 줄이 서고 체크는 같은 규칙 ·
 *  closing 이 있으면 마지막 문장 시작에 «오늘도 다 같이 / 무사히 퇴근합시다!(초록)». */
export const MorningChecks: React.FC<Base & { kicker: string; title: string; items: Array<{ text: string; sub?: string }>; mode?: 'basics' | 'chant'; closing?: string[] }> = ({
  f, beats, kicker, title, items, mode = 'basics', closing,
}) => {
  const { fps, px } = useScale();
  const chant = mode === 'chant';
  const rowH = chant ? 130 : 135, step = chant ? 150 : 155, top = chant ? 270 : 275; // 아래끝 ≤ 722(자막 두 줄 윗선 ≈746 − 틈) — 10-11 실측
  const showAt = chant ? beatStart(beats, 1, Math.round(2.5 * fps)) : 0;
  const closeAt = closing ? beatStart(beats, items.length + 1, Math.round((3 + items.length * 2.4) * fps)) : Infinity;
  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      <div style={rise(f, 0, fps)}><Head kicker={kicker} title={title} px={px} /></div>
      {items.map((it, i) => {
        const y = top + i * step;
        const tickAt = beatEnd(beats, 1 + i, Math.round((1.6 + i * 2.6 + 2.4) * fps)) - Math.round(0.5 * fps);
        const on = f >= tickAt;
        return (
          <div key={i} style={{ position: 'absolute', inset: 0, ...rise(f, showAt + Math.round((chant ? 0.3 * i : 0.2 + 0.3 * i) * fps), fps) }}>
            <Abs x={X0} y={y} w={X1 - X0} h={rowH} px={px} style={{ borderRadius: px(R), background: MORNING.card, overflow: 'hidden' }}>
              {on ? <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: px(12), background: MORNING.green }} /> : null}
            </Abs>
            <CheckBox x={X0 + (chant ? 40 : 44)} y={y + (chant ? 30 : 33)} on={on} px={px} />
            <Abs x={X0 + (chant ? 145 : 150)} y={y + (chant ? 35 : 20)} px={px}><Txt size={58} weight={700} color={MORNING.ink} px={px} fitW={W - 190} style={{ whiteSpace: 'nowrap' }}>{it.text}</Txt></Abs>
            {it.sub && !chant ? (
              <Abs x={X0 + 152} y={y + 86} px={px}><Txt size={34} weight={500} color={MORNING.sub} px={px} fitW={W - 192} style={{ whiteSpace: 'nowrap' }}>{it.sub}</Txt></Abs>
            ) : null}
          </div>
        );
      })}
      {closing && f >= closeAt - 1 ? (
        <div style={{ position: 'absolute', inset: 0, ...rise(f, closeAt, fps) }}>
          <Abs x={X0} y={top + items.length * step - 5} px={px}><Txt size={64} weight={700} color={MORNING.ink} px={px} fitW={W} style={{ whiteSpace: 'nowrap' }}>{closing[0]}</Txt></Abs>
          <Abs x={X0} y={top + items.length * step + 71} px={px}><Txt size={64} weight={700} color={MORNING.green} px={px} fitW={W} style={{ whiteSpace: 'nowrap' }}>{closing[1]}</Txt></Abs>
        </div>
      ) : null}
    </div>
  );
};

/** ③ 날씨 — 흰 카드(해 + 하늘 + 아침→낮 + 한 줄) · 문장 1 시작에 노란 팁 띠. 해는 그림이 아니라 코드로 그린다(0원). */
export const MorningWeather: React.FC<Base & { kicker: string; title?: string; sky: string; tmin: number; tmax: number; detail?: string; tip?: string }> = ({
  f, beats, kicker, title = '오늘의 날씨', sky, tmin, tmax, detail, tip,
}) => {
  const { fps, px } = useScale();
  const tipAt = beatStart(beats, 1, Math.round(1.6 * fps));
  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      <div style={rise(f, 0, fps)}><Head kicker={kicker} title={title} px={px} /></div>
      <div style={{ position: 'absolute', inset: 0, ...rise(f, 0, fps) }}>
        <Abs x={X0} y={265} w={X1 - X0} h={320} px={px} style={{ borderRadius: px(R), background: MORNING.card }} />
        <Abs x={X0 + 60} y={320} w={210} h={210} px={px} style={{ borderRadius: '50%', background: MORNING.sun }} />
        <Abs x={X0 + 330} y={295} px={px}><Txt size={66} weight={700} color={MORNING.ink} px={px} fitW={W - 370} style={{ whiteSpace: 'nowrap' }}>{sky}</Txt></Abs>
        <Abs x={X0 + 330} y={380} px={px}><Txt size={76} weight={700} color={MORNING.ink} px={px} fitW={W - 370} style={{ whiteSpace: 'nowrap' }}>{`아침 ${tmin}°  →  낮 ${tmax}°`}</Txt></Abs>
        {detail ? <Abs x={X0 + 330} y={485} px={px}><Txt size={40} weight={500} color={MORNING.sub} px={px} fitW={W - 370} style={{ whiteSpace: 'nowrap' }}>{detail}</Txt></Abs> : null}
      </div>
      {tip && f >= tipAt - 1 ? (
        <div style={{ position: 'absolute', inset: 0, ...rise(f, tipAt, fps) }}>
          <Abs x={X0} y={610} w={X1 - X0} h={110} px={px} style={{ borderRadius: px(R), background: MORNING.tipBg, display: 'flex', alignItems: 'center', paddingLeft: px(44) }}>
            <Txt size={48} weight={700} color={MORNING.tipInk} px={px} fitW={W - 88} style={{ whiteSpace: 'nowrap' }}>{tip}</Txt>
          </Abs>
        </div>
      ) : null}
    </div>
  );
};

/** ④ 오늘 작업 목록 — 문장 1 시작에 focus 줄만 빨간 테두리 + «특히 주의» 태그 · 나머지 글자 흐림 · 아래 한 줄. 단계 점수 없음. */
export const MorningFocus: React.FC<Base & { kicker: string; title: string; items: string[]; focus: number; note?: string; tag?: string }> = ({
  f, beats, kicker, title, items, focus, note, tag = '특히 주의',
}) => {
  const { fps, px } = useScale();
  // 강조는 둘째 문장(«…, ○○ 구간입니다») 시작에 · 한 문장으로 말하면 그 문장 가운데(10-11 3차 실측: 한 문장 줄에서 강조가 장면 끝까지 안 켜짐).
  const b0 = beats?.[0];
  const hitAt = beats?.[1]?.atFrame ?? (b0 && b0.untilFrame != null ? Math.round((b0.atFrame + b0.untilFrame) / 2) : Math.round(4.5 * fps));
  const h = Math.max(0, Math.min(1, (f - hitAt) / (0.5 * fps)));
  const rowH = 115, step = 135, top = 275; // 아래끝(설명 줄 포함) ≤ 722 — 10-11 실측
  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      <div style={rise(f, 0, fps)}><Head kicker={kicker} title={title} px={px} /></div>
      {items.map((job, i) => {
        const y = top + i * step;
        const isFocus = i === focus;
        const dim = !isFocus && h > 0;
        return (
          <div key={i} style={{ position: 'absolute', inset: 0, ...rise(f, Math.round((0.3 + 0.35 * i) * fps), fps) }}>
            {isFocus && h > 0 ? <Abs x={X0 - 6} y={y - 6} w={X1 - X0 + 12} h={rowH + 12} px={px} style={{ borderRadius: px(30), background: MORNING.red, opacity: h }} /> : null}
            <Abs x={X0} y={y} w={X1 - X0} h={rowH} px={px} style={{ borderRadius: px(R), background: MORNING.card }} />
            <Abs x={X0 + 50} y={y + 22} px={px}><Txt size={60} weight={700} color={dim ? MORNING.dim : MORNING.ink} px={px} fitW={W - 50 - 330} style={{ whiteSpace: 'nowrap', transition: 'none' }}>{job}</Txt></Abs>
            {isFocus && h > 0 ? (
              <Abs x={X1 - 300} y={y + 10} w={260} h={95} px={px} style={{ borderRadius: px(20), background: MORNING.red, display: 'flex', alignItems: 'center', justifyContent: 'center', ...rise(f, hitAt, fps) }}>
                <Txt size={50} weight={700} color="#FFFFFF" px={px} style={{ whiteSpace: 'nowrap' }}>{tag}</Txt>
              </Abs>
            ) : null}
          </div>
        );
      })}
      {note && h > 0 ? (
        <div style={{ position: 'absolute', inset: 0, ...rise(f, hitAt, fps) }}>
          <Abs x={X0} y={top + items.length * step - 8} px={px}><Txt size={40} weight={600} color={MORNING.sub} px={px} fitW={W} style={{ whiteSpace: 'nowrap' }}>{note}</Txt></Abs>
        </div>
      ) : null}
    </div>
  );
};

/** ⑤ 최근 같은 작업 사고 — 그림 2장(둥근 모서리) · 문장 1 시작에 «무슨 일»(먹색) + «결과»(빨강). 출처 장표는 두지 않는다(회장님 msg 5388). */
export const MorningCase: React.FC<Base & { kicker: string; title?: string; images: string[]; what: string; harm?: string }> = ({
  f, beats, kicker, title = '최근 같은 작업 사고', images, what, harm,
}) => {
  const { fps, px } = useScale();
  const resAt = beatStart(beats, 1, Math.round(1.2 * fps));
  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      <div style={rise(f, 0, fps)}><Head kicker={kicker} title={title} px={px} /></div>
      {images.slice(0, 2).map((src, i) => (
        <div key={i} style={{ position: 'absolute', inset: 0, ...rise(f, Math.round(0.5 * i * fps), fps) }}>
          <Abs x={X0 + i * 494} y={262} w={470} h={280} px={px} style={{ borderRadius: px(22), overflow: 'hidden', background: MORNING.box }}>
            <SceneImg src={src} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </Abs>
        </div>
      ))}
      {f >= resAt - 1 ? (
        <div style={{ position: 'absolute', inset: 0, ...rise(f, resAt, fps) }}>
          <Abs x={X0} y={565} px={px}><Txt size={62} weight={700} color={MORNING.ink} px={px} fitW={W} style={{ whiteSpace: 'nowrap' }}>{what}</Txt></Abs>
          {harm ? <Abs x={X0} y={645} px={px}><Txt size={62} weight={700} color={MORNING.red} px={px} fitW={W} style={{ whiteSpace: 'nowrap' }}>{harm}</Txt></Abs> : null}
        </div>
      ) : null}
    </div>
  );
};

/** ⑥ 오늘 꼭 두 가지 — 카드 k(그림 + 빨간 번호 + 문구)가 문장 1+k 시작에 선다(두 번째 카드 = 두 번째 문장 시작). */
export const MorningActions: React.FC<Base & { kicker: string; title?: string; cards: Array<{ image?: string; text: string }> }> = ({
  f, beats, kicker, title = '오늘 꼭 두 가지', cards,
}) => {
  const { fps, px } = useScale();
  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      <div style={rise(f, 0, fps)}><Head kicker={kicker} title={title} px={px} /></div>
      {cards.slice(0, 2).map((c, i) => {
        const at = beatStart(beats, 1 + i, Math.round((i === 0 ? 0.2 : 5.7) * fps));
        if (f < at - 1) return null;
        const x = X0 + i * 490;
        return (
          <div key={i} style={{ position: 'absolute', inset: 0, ...rise(f, at, fps) }}>
            <Abs x={x} y={265} w={460} h={455} px={px} style={{ borderRadius: px(R), background: MORNING.card }} />
            {c.image ? (
              <Abs x={x + 20} y={285} w={420} h={240} px={px} style={{ borderRadius: px(22), overflow: 'hidden', background: MORNING.box }}>
                <SceneImg src={c.image} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </Abs>
            ) : null}
            <Abs x={x + 36} y={535} px={px}><Txt size={44} weight={700} color={MORNING.red} px={px}>{String(i + 1)}</Txt></Abs>
            <Abs x={x + 36} y={585} w={400} px={px}><Txt size={50} weight={700} color={MORNING.ink} px={px} fitW={400} style={{ whiteSpace: 'pre' }}>{twoLines(c.text)}</Txt></Abs>
          </div>
        );
      })}
    </div>
  );
};

/** 장표 종류 → 컴포넌트(화면 전용 · `slide-components.ts` 가 얹는다). */
export const MORNING_SLIDE_COMPONENTS = {
  morningCover: MorningCover,
  morningChecks: MorningChecks,
  morningWeather: MorningWeather,
  morningFocus: MorningFocus,
  morningCase: MorningCase,
  morningActions: MorningActions,
} as const;
